import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, Clock, Coins, Info, Minus, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SupportTrigger } from "@/components/SupportTrigger";
import { COIN_PRICE_CENTS, MIN_COINS, formatEuro, grossFromNet } from "@/lib/salonturbo";
import {
  costRows,
  costTotals,
  includedFeatures,
  OWNER_HOURLY_RATE_EUR,
  SCENARIO_BOOST_DAYS,
} from "@/lib/pricing-content";
import { homeFaqs, faqJsonLd } from "@/lib/faq-content";
import salonInterior from "@/assets/salon-interior.jpg";

const euro = (value: number) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);

export const Route = createFileRoute("/preise")({
  head: () => ({
    meta: [
      { title: "Preise – 25 € pro Boost-Tag, ohne Agentur" },
      {
        name: "description",
        content:
          "1 Coin = 25 € netto = 1 geboosteter Tag inklusive Werbebudget, Kampagne und Salon-Seite. Transparenter Vergleich zu Agenturkosten – inklusive deiner Zeit.",
      },
      { property: "og:title", content: "Preise – 25 € pro Boost-Tag, ohne Agentur" },
      {
        property: "og:description",
        content:
          "Keine Mindestlaufzeit, kein Retainer: Du zahlst nur die Tage, die du füllen willst. Mit Kostenvergleich zur Agentur.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqJsonLd(homeFaqs)) }],
  }),
  component: PricingPage,
});

function PricingPage() {
  const saving = costTotals.agency - costTotals.salonturbo;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section className="border-b bg-secondary/40">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <div>
                <p className="eyebrow text-muted-foreground">Preise</p>
                <h1 className="mt-4 font-display text-3xl font-light leading-tight sm:text-5xl">
                  Ein Preis, keine Vertragsbindung
                </h1>
                <div className="mt-6 h-px w-16 bg-primary" />
                <p className="mt-6 max-w-xl text-muted-foreground">
                  Du zahlst nur für die Tage, die du wirklich füllen willst. Ein Coin steht für einen
                  geboosteten Tag – Kampagne, Werbebudget und deine Salon-Seite sind enthalten.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild size="lg">
                    <Link to="/auth">
                      Jetzt starten <ArrowRight className="ml-1 size-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <Link to="/faq">Häufige Fragen</Link>
                  </Button>
                </div>
              </div>

              <div className="rounded-3xl border bg-card p-6 shadow-soft sm:p-8">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Coins className="size-4 text-primary" />1 Coin = 1 Boost-Tag
                </div>
                <p className="mt-4 font-display text-5xl font-light">
                  {formatEuro(COIN_PRICE_CENTS)}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  netto pro Coin · {formatEuro(grossFromNet(COIN_PRICE_CENTS))} brutto inkl. 19 %
                  MwSt.
                </p>
                <div className="mt-6 space-y-3 border-t pt-6 text-sm">
                  <Row label="Mindestabnahme" value={`${MIN_COINS} Coins`} />
                  <Row
                    label="Erste Aufladung"
                    value={`${formatEuro(COIN_PRICE_CENTS * MIN_COINS)} netto`}
                  />
                  <Row label="Laufzeit" value="keine" />
                  <Row label="Gültigkeit der Coins" value="unbegrenzt" />
                </div>
                <Button asChild size="lg" className="mt-6 w-full">
                  <Link to="/auth">Salon einrichten</Link>
                </Button>
                <p className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck className="size-3.5" /> Zahlung über Stripe: Karte, Apple Pay, Google
                  Pay, SEPA
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Inklusive */}
        <section className="mx-auto max-w-6xl px-4 py-16">
          <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div>
              <p className="eyebrow text-muted-foreground">Im Coin enthalten</p>
              <h2 className="mt-4 font-display text-2xl font-light sm:text-4xl">
                Alles drin, ohne Zusatzkosten
              </h2>
              <div className="mt-6 h-px w-16 bg-primary" />
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {includedFeatures.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="overflow-hidden rounded-3xl border">
              <img
                src={salonInterior}
                alt="Friseursalon mit freien Plätzen am Nachmittag"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Kostenvergleich */}
        <section className="border-y bg-secondary/40">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:py-20">
            <div className="text-center">
              <p className="eyebrow text-muted-foreground">Kostenvergleich</p>
              <h2 className="mt-4 font-display text-2xl font-light sm:text-4xl">
                Agentur/selbst machen – und SalonTurbo
              </h2>
              <div className="mx-auto mt-6 h-px w-16 bg-primary" />
              <p className="mx-auto mt-6 max-w-2xl text-sm text-muted-foreground">
                Gerechnet auf {SCENARIO_BOOST_DAYS} geboostete Tage im Monat. Deine eigene Zeit ist
                mit {OWNER_HOURLY_RATE_EUR} € pro Stunde bewertet – denn Anzeigen tagesaktuell ein-
                und auszuschalten kostet dich Arbeitszeit im Salon.
              </p>
            </div>

            <div className="mt-10 overflow-hidden rounded-3xl border bg-card shadow-soft">
              <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 border-b bg-secondary/50 px-4 py-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:px-6">
                <span>Position</span>
                <span>Agentur / selbst</span>
                <span className="text-primary">SalonTurbo</span>
              </div>
              {costRows.map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 border-b px-4 py-4 text-sm last:border-0 sm:px-6"
                >
                  <div>
                    <p className="font-medium">{row.label}</p>
                    {row.note && <p className="mt-1 text-xs text-muted-foreground">{row.note}</p>}
                  </div>
                  <span className="text-muted-foreground">{row.agency}</span>
                  <span className="font-medium">{row.salonturbo}</span>
                </div>
              ))}
              <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 bg-espresso px-4 py-5 text-espresso-foreground sm:px-6">
                <span className="font-display text-base sm:text-lg">Summe pro Monat</span>
                <span className="font-display text-base sm:text-lg">
                  ca. {euro(costTotals.agency)}
                </span>
                <span className="font-display text-base text-primary sm:text-lg">
                  ca. {euro(costTotals.salonturbo)}
                </span>
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <Highlight
                icon={<Minus className="size-4 text-primary" />}
                title={`ca. ${euro(saving)} weniger`}
                text={`Differenz pro Monat im Szenario mit ${SCENARIO_BOOST_DAYS} Boost-Tagen.`}
              />
              <Highlight
                icon={<Clock className="size-4 text-primary" />}
                title="Minuten statt Stunden"
                text="Einmal pro Woche einloggen, Tage anklicken, fertig. Kein Kampagnen-Management."
              />
              <Highlight
                icon={<ShieldCheck className="size-4 text-primary" />}
                title="Kein Retainer"
                text="Keine Mindestlaufzeit von 6 bis 12 Monaten, keine monatliche Grundgebühr."
              />
            </div>

            <p className="mt-6 flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" />
              Marktübliche Richtwerte für Agentur- und Freelancer-Leistungen, keine Angebote.
              Einmalkosten für Setup und Landingpage sind auf zwölf Monate verteilt. Deine
              tatsächlichen Kosten können abweichen.
            </p>
          </div>
        </section>

        {/* Rechenbeispiel */}
        <section className="mx-auto max-w-5xl px-4 py-16">
          <p className="eyebrow text-center text-muted-foreground">Zur Einordnung</p>
          <h2 className="mt-4 text-center font-display text-2xl font-light sm:text-4xl">
            Was ein gefüllter Stuhl ausmacht
          </h2>
          <div className="mx-auto mt-6 h-px w-16 bg-primary" />
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            <ExampleCard
              step="1"
              title="Ein Boost-Tag"
              text={`Kostet dich ${formatEuro(COIN_PRICE_CENTS)} netto – inklusive Werbebudget.`}
            />
            <ExampleCard
              step="2"
              title="Zusätzliche Termine"
              text="Schon zwei bis drei spontane Termine mit Schnitt und Föhnen liegen in vielen Salons deutlich über diesem Betrag."
            />
            <ExampleCard
              step="3"
              title="Und danach"
              text="Wer über die Salon-Seite kommt, kommt oft wieder – der Wert eines Neukunden endet nicht beim ersten Besuch."
            />
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            Beispielrechnung zur Einordnung. Wir versprechen keine bestimmte Anzahl an Anrufen oder
            Terminen – wie gut ein Tag läuft, hängt von Region, Wettbewerb und deinem Angebot ab.
          </p>
        </section>

        {/* FAQ-Anriss */}
        <section className="border-t bg-secondary/40">
          <div className="mx-auto max-w-4xl px-4 py-16">
            <h2 className="text-center font-display text-2xl font-light sm:text-3xl">
              Häufige Fragen zum Preis
            </h2>
            <div className="mt-8 space-y-3">
              {homeFaqs.slice(3, 6).map((faq) => (
                <div key={faq.question} className="rounded-2xl border bg-card p-5">
                  <p className="font-display text-base font-medium sm:text-lg">{faq.question}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{faq.answer}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild variant="outline">
                <Link to="/faq">Alle Fragen ansehen</Link>
              </Button>
              <SupportTrigger variant="ghost" label="Support fragen" />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-espresso text-espresso-foreground">
          <div className="mx-auto max-w-4xl px-4 py-20 text-center">
            <h2 className="font-display text-3xl font-light sm:text-4xl">
              Starte mit {MIN_COINS} Coins
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm text-espresso-foreground/70">
              {formatEuro(COIN_PRICE_CENTS * MIN_COINS)} netto für vier Tage, an denen dein Salon
              genau dort auftaucht, wo spontan gesucht wird.
            </p>
            <Button asChild size="lg" className="mt-8">
              <Link to="/auth">
                Jetzt starten <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function Highlight({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded-2xl border bg-card p-5">
      <div className="flex size-9 items-center justify-center rounded-full bg-primary/10">
        {icon}
      </div>
      <p className="mt-3 font-display text-base font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

function ExampleCard({ step, title, text }: { step: string; title: string; text: string }) {
  return (
    <div className="rounded-3xl border bg-card p-6">
      <span className="font-display text-sm text-primary">{step}</span>
      <p className="mt-3 font-display text-lg font-normal">{title}</p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
    </div>
  );
}
